import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  MapPin,
  Clock,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20he%20leido%20tu%20articulo%20y%20me%20gustaria%20revisar%20mi%20caso";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

export default function BlogArticle1() {
  useEffect(() => {
    document.title = "Cómo mejorar decisiones empresariales con datos | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Descubre cómo el análisis de datos puede ayudarte a tomar mejores decisiones en tu empresa y aumentar la rentabilidad.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/blog/analisis-datos-empresas-decisiones");
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="blog-article-page">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="article-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="article-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Análisis de Datos" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/proceso" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Proceso</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-indigo-400 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Article Header */}
        <section className="relative pt-32 pb-12 px-6 overflow-hidden" data-testid="article-header">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />

          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={fadeInUp}>
                <Link to="/blog" className="inline-flex items-center text-slate-400 hover:text-indigo-400 text-sm mb-8 transition-colors gap-2" data-testid="article-back-link">
                  <ArrowLeft className="w-4 h-4" />
                  Volver al blog
                </Link>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">
                  Estrategia de datos
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  5 min de lectura
                </span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-6" data-testid="article-h1">
                Tu empresa está perdiendo dinero… y probablemente no lo sabes
              </motion.h1>
            </motion.div>
          </div>
        </section>

        {/* Article Content */}
        <article className="relative px-6 pb-16" data-testid="article-content">
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="article-body"
            >
              {/* Intro */}
              <motion.div variants={fadeInUp} className="mb-12">
                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-6">
                  No es una exageración.
                </p>
                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-6">
                  Es lo que ocurre en la mayoría de empresas.
                </p>
                <p className="text-slate-400 leading-relaxed mb-2">No porque funcionen mal.</p>
                <p className="text-slate-400 leading-relaxed mb-2">No porque no vendan.</p>
                <p className="text-slate-400 leading-relaxed mb-6">No porque no tengan datos.</p>
                <p className="text-slate-300 leading-relaxed font-medium">
                  Sino porque están tomando decisiones sin entender realmente qué está pasando.
                </p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: El problema no es la falta de datos */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-1">El problema no es la falta de datos</h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Hoy en día, cualquier empresa tiene información de sobra:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    ventas
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    clientes
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    costes
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    campañas
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    operaciones
                  </li>
                </ul>
                <p className="text-slate-400 leading-relaxed mb-6">El problema es otro.</p>
                <p className="text-slate-300 leading-relaxed font-medium">No saben qué hacer con todo eso.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: "Nos va bien" no significa que lo estés haciendo bien */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-2">"Nos va bien" no significa que lo estés haciendo bien</h2>
                <p className="text-slate-400 leading-relaxed mb-6">Muchas empresas dicen:</p>
                <blockquote className="border-l-4 border-indigo-500 pl-6 py-3 mb-6 bg-slate-900/50 rounded-r-lg">
                  <p className="text-slate-300 text-lg italic">"Nos va bien"</p>
                </blockquote>
                <p className="text-slate-400 leading-relaxed mb-6">Y es cierto.</p>
                <p className="text-slate-400 leading-relaxed mb-2">Facturan.</p>
                <p className="text-slate-400 leading-relaxed mb-2">Tienen clientes.</p>
                <p className="text-slate-400 leading-relaxed mb-6">El negocio funciona.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Pero eso no significa que estén optimizando nada.</p>
                <p className="text-slate-400 leading-relaxed mb-6">De hecho, muchas veces significa justo lo contrario:</p>
                <p className="text-slate-300 leading-relaxed font-medium text-lg">
                  👉 que están ganando dinero <strong className="text-indigo-400">a pesar de sus decisiones, no gracias a ellas</strong>
                </p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: El dinero que estás perdiendo no se ve */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-3">El dinero que estás perdiendo no se ve</h2>
                <p className="text-slate-400 leading-relaxed mb-2">No hay una alerta.</p>
                <p className="text-slate-400 leading-relaxed mb-6">No hay un error visible.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Pero está ahí:</p>
                <ul className="space-y-2 mb-6">
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    decisiones que podrían ser mejores
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    inversión mal optimizada
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    procesos que podrían ser más eficientes
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    oportunidades que ni siquiera sabes que existen
                  </li>
                </ul>
                <p className="text-slate-400 leading-relaxed mb-6">Y todo eso tiene un impacto directo.</p>
                <p className="text-slate-400 leading-relaxed mb-2">En tus márgenes.</p>
                <p className="text-slate-400 leading-relaxed mb-2">En tu crecimiento.</p>
                <p className="text-slate-400 leading-relaxed">En tu rentabilidad.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: La intuición tiene un límite */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-4">La intuición tiene un límite</h2>
                <p className="text-slate-400 leading-relaxed mb-6">La experiencia es importante.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Pero cuando el negocio crece, la intuición deja de ser suficiente.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Porque:</p>
                <ul className="space-y-2 mb-6">
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    hay demasiadas variables
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    demasiados datos
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    demasiadas decisiones
                  </li>
                </ul>
                <p className="text-slate-300 leading-relaxed font-medium">Y seguir decidiendo "como siempre" empieza a salir caro.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Tener informes no sirve de nada */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-5">Tener informes no sirve de nada</h2>
                <p className="text-slate-400 leading-relaxed mb-6">Muchas empresas ya tienen:</p>
                <ul className="space-y-2 mb-6">
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    dashboards
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    informes
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    hojas de cálculo
                  </li>
                </ul>
                <p className="text-slate-400 leading-relaxed mb-6">Y aun así no saben responder a preguntas básicas:</p>
                <ul className="space-y-2 mb-6">
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    ¿qué clientes son realmente rentables?
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    ¿qué productos o servicios compensan de verdad?
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    ¿dónde se está perdiendo dinero?
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    ¿qué decisiones están funcionando?
                  </li>
                </ul>
                <p className="text-slate-300 leading-relaxed font-medium">Si no puedes responder eso con claridad, tienes un problema.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Aquí es donde se pierde el dinero de verdad */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-6">Aquí es donde se pierde el dinero de verdad</h2>
                <p className="text-slate-400 leading-relaxed mb-6">No en una gran mala decisión.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Sino en muchas pequeñas decisiones "suficientemente buenas".</p>
                <p className="text-slate-400 leading-relaxed mb-6">Porque esas son las que:</p>
                <ul className="space-y-2 mb-6">
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    se repiten
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    no se cuestionan
                  </li>
                  <li className="text-slate-400 flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    y nunca se optimizan
                  </li>
                </ul>
                <p className="text-slate-300 leading-relaxed font-medium">Y ahí es donde se acumula la pérdida.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Las empresas que crecen no hacen más, hacen mejor */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-7">Las empresas que crecen no hacen más, hacen mejor</h2>
                <p className="text-slate-400 leading-relaxed mb-2">No trabajan más.</p>
                <p className="text-slate-400 leading-relaxed mb-2">No tienen más datos.</p>
                <p className="text-slate-400 leading-relaxed mb-6">No tienen mejores herramientas.</p>
                <p className="text-slate-300 leading-relaxed font-medium mb-6">Toman mejores decisiones.</p>
                <p className="text-slate-400 leading-relaxed">Y eso viene de entender qué está pasando realmente en su negocio.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Una pregunta incómoda */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-8">Una pregunta incómoda</h2>
                <p className="text-slate-400 leading-relaxed mb-6">Si ahora mismo tuvieras que decir:</p>
                <div className="space-y-3 mb-6 pl-4 border-l-2 border-indigo-500/30">
                  <p className="text-slate-300">👉 qué parte de tu negocio es la más rentable</p>
                  <p className="text-slate-300">👉 qué decisiones están generando más impacto</p>
                  <p className="text-slate-300">👉 dónde estás perdiendo dinero</p>
                </div>
                <p className="text-slate-400 leading-relaxed mb-6">¿lo sabrías con certeza?</p>
                <p className="text-slate-400 leading-relaxed mb-2">No con intuición.</p>
                <p className="text-slate-300 leading-relaxed font-medium">Con datos.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Si la respuesta es "más o menos" */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-9">Si la respuesta es "más o menos"</h2>
                <p className="text-slate-400 leading-relaxed mb-6">Entonces tienes margen de mejora.</p>
                <p className="text-slate-300 leading-relaxed font-medium">Y ese margen, casi siempre, es dinero.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: No necesitas más datos */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-10">No necesitas más datos</h2>
                <p className="text-slate-400 leading-relaxed mb-6">Necesitas dejar de ignorar los que ya tienes.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Porque el problema no es la información.</p>
                <p className="text-slate-300 leading-relaxed font-medium">Es no utilizarla para decidir mejor.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Y eso tiene solución */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-11">Y eso tiene solución</h2>
                <p className="text-slate-400 leading-relaxed mb-2">Sin sistemas complejos.</p>
                <p className="text-slate-400 leading-relaxed mb-2">Sin proyectos eternos.</p>
                <p className="text-slate-400 leading-relaxed mb-6">Sin complicaciones innecesarias.</p>
                <p className="text-slate-300 leading-relaxed font-medium">Simplemente entendiendo mejor tu negocio.</p>
              </motion.div>

              <motion.hr variants={fadeInUp} className="border-slate-800 my-12" />

              {/* Section: Si quieres verlo claro */}
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article-h2-12">Si quieres verlo claro</h2>
                <p className="text-slate-400 leading-relaxed mb-8">
                  Si crees que puedes estar perdiendo oportunidades sin darte cuenta, tiene sentido revisarlo.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </article>

        {/* CTA Section */}
        <section className="relative section-padding bg-gradient-to-b from-slate-900/80 to-[#020617]" data-testid="article-cta">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.p variants={fadeInUp} className="text-indigo-400 font-medium mb-4 text-sm flex items-center justify-center gap-2">
                <BookOpen className="w-4 h-4" />
                Da el primer paso
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold mb-4">
                Escríbeme y <span className="text-gradient">vemos tu caso</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-4">
                Sin compromiso.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-8 max-w-lg mx-auto">
                Porque muchas veces, el problema no es lo que falta… sino lo que ya tienes y no estás utilizando.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full">
                  <Link to="/contacto" data-testid="article-cta-contact">
                    Solicitar auditoría gratuita
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="article-cta-whatsapp">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Contactar por WhatsApp
                  </a>
                </Button>
              </motion.div>

              {/* Internal links */}
              <motion.div variants={fadeInUp} className="mt-8 text-sm text-slate-500 text-center space-y-2">
                <p>Sigue leyendo: <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Cómo saber si tu empresa está perdiendo dinero</Link></p>
                <p>Servicios: <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Análisis de datos para empresas</Link> · <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Business Intelligence en Murcia</Link> · <Link to="/consultor-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Consultor de datos en Murcia</Link></p>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="article-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="article-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
